namespace appn.dataTable {

    export interface EditorArgs {
        entityId: string;
        updateUrl: string;
        pkColumns: string[];
        canEdit: boolean;
        lastUpdatedColumnName?: string;
        columnTypes: { [key: string]: string };
        requiredColumns: string[];
        columns: { data: number, title: string, sortable: boolean }[];
        onChangesApplied?: (resp: any) => void;
    }

    export class RowStateStore {
        public pendingUpdates = new Map<string, Record<string, string>>();
        public newRows = new Map<string, Record<string, string>>();
        public deletedRows = new Set<string>();
        public selectedRows = new Set<string>();
        public originalData = new Map<string, Record<string, string>>();
        private onStateChanged: () => void;

        constructor(onStateChanged: () => void) {
            this.onStateChanged = onStateChanged;
        }

        public updateCell(pk: string, col: string, val: string) {
            pk = String(pk);
            if (this.deletedRows.has(pk))
                this.deletedRows.delete(pk);

            if (pk.startsWith('new_row_')) {
                const row = this.newRows.get(pk);
                if (row)
                    row[col] = val;
            } else {
                const origVal = this.originalData.get(pk)?.[col] || "";
                if (val === origVal) {
                    if (this.pendingUpdates.has(pk)) {
                        delete this.pendingUpdates.get(pk)![col];
                        if (Object.keys(this.pendingUpdates.get(pk)!).length === 0)
                            this.pendingUpdates.delete(pk);
                    }
                } else {
                    if (!this.pendingUpdates.has(pk))
                        this.pendingUpdates.set(pk, {});
                    this.pendingUpdates.get(pk)![col] = val;
                }
            }
            this.onStateChanged();
        }

        public clearAll() {
            this.pendingUpdates.clear();
            this.newRows.clear();
            this.deletedRows.clear();
            this.selectedRows.clear();
            this.originalData.clear();
            this.onStateChanged();
        }

        public getChangesCount(): number {
            return this.pendingUpdates.size + this.newRows.size + this.deletedRows.size;
        }
    }

    export class Editor {
        private args: EditorArgs;
        private dataTable: any;
        private reviewDataTable: any;
        private newRowCounter = 0;
        private isChangesView = false;
        public store: RowStateStore;

        constructor(args: EditorArgs) {
            this.args = args;
            this.store = new RowStateStore(() => this.syncUIWithState());
        }

        public init(datatable: any) {
            this.dataTable = datatable;
            this.createReviewDataTable();
            this.bindEvents();
        }

        private getColumnWidthByType(type: string): string {
            switch (type) {
                case 'Boolean': return '40px';
                case 'Number': return '80px';
                case 'Date': return '200px';
                case 'LongText': return '400px';
                case 'UniqueIdentifier': return '350px';
                case 'Text':
                default: return '250px';
            }
        }

        private renderTemplate(templateId: string, tokens: Record<string, string>): string {
            let html = document.getElementById(templateId)?.innerHTML || '';
            for (const [key, value] of Object.entries(tokens)) {
                const regex = new RegExp(`{${key}}`, 'g');
                html = html.replace(regex, value ? value.toString().replace(/</g, "&lt;").replace(/>/g, "&gt;") : "");
            }
            return html;
        }

        private getColumnTitleHtml(colName: string, type: string, isPk: boolean): string {
            let titleHtml = colName;
            if (isPk) titleHtml += " <i class='fa fa-key text-muted text-xs ml-1' title='Primary Key'></i>";
            titleHtml += `<div class='text-muted text-xs font-weight-normal'>${type}</div>`;
            return titleHtml;
        }

        private renderCellHtml(pkHash: string, colName: string, val: string, type: string, isPk: boolean, isLastUpdated: boolean, isNewRow: boolean, inputClass: string = ''): string {
            if (this.isChangesView && this.store.deletedRows.has(pkHash)) {
                return this.renderTemplate('tpl-cell-deleted', { col: colName, pk: pkHash, valDisplay: val, type: type });
            }

            if (this.isChangesView && this.store.pendingUpdates.has(pkHash) && this.store.pendingUpdates.get(pkHash)![colName] !== undefined) {
                const newVal = this.store.pendingUpdates.get(pkHash)![colName];
                return this.renderTemplate('tpl-cell-changed', { col: colName, pk: pkHash, originalVal: val, newVal: newVal, type: type });
            }

            const isReadOnly = (isPk && !isNewRow) || isLastUpdated || !this.args.canEdit;
            if (isReadOnly)
                return this.renderTemplate('tpl-cell-readonly', { valDisplay: val || (isLastUpdated ? T('Never') : ''), valRaw: val, col: colName, pk: pkHash, type: type });

            if (type === 'Number')
                return this.renderTemplate('tpl-cell-number', { valRaw: val, col: colName, pk: pkHash, type: type });
            
            if (type === 'Boolean')
                return this.renderTemplate('tpl-cell-boolean', { valRaw: val, col: colName, pk: pkHash, type: type, inputClass: inputClass })

            const placeholder = type === 'Date' ? 'yyyy-MM-ddTHH:mm:ssZ' : '';
            return this.renderTemplate('tpl-cell-contenteditable', { valRaw: val, col: colName, pk: pkHash, placeholder: placeholder, type: type });
        }
        
        public getMainColumns(): any[] {
            return [
                {
                    data: null,
                    title: this.renderCellHtml('select-all-rows', '', '', 'Boolean', false, false, false, 'select-all-rows'),
                    bSortable: false,
                    className: 'text-center selectRow-column',
                    width: '40px',
                    sWidth: '40px',
                    render: (data: any, typeInfo: string, row: any[]) => {
                        if (typeInfo !== 'display' || !row || row.length === 0) return '';
                        return this.renderCellHtml(String(row[0]), '', '', 'Boolean', false, false, false, 'selectRow');
                    }
                },
                ...this.args.columns.map((colDef) => {
                    const colName = colDef.title;
                    const colIndex = colDef.data;
                    const type = this.args.columnTypes[colName];
                    const isPk = this.args.pkColumns.includes(colName);
                    const isLastUpdated = colName === this.args.lastUpdatedColumnName;

                    return {
                        data: colIndex,
                        title: this.getColumnTitleHtml(colName, type, isPk),
                        bSortable: colDef.sortable,
                        render: (data: any, typeInfo: string, row: any[]) => {
                            if (!row || row.length === 0) return typeInfo !== 'display' ? data : "";

                            const pkHash = String(row[0]);
                            const valStr = data === null || data === undefined ? "" : data.toString();

                            if (typeInfo === 'display' && pkHash && !pkHash.startsWith('new_row_')) {
                                if (!this.store.originalData.has(pkHash)) this.store.originalData.set(pkHash, {});
                                if (this.store.originalData.get(pkHash)![colName] === undefined)
                                    this.store.originalData.get(pkHash)![colName] = valStr;
                            }

                            if (typeInfo !== 'display') return data;

                            return this.renderCellHtml(pkHash, colName, valStr, type, isPk, isLastUpdated || false, false, '');
                        }
                    };
                })
            ];
        }

        public renderRowHtml(rowData: any, pkHash: string): string {
            pkHash = String(pkHash);
            const isNewRow = pkHash.startsWith('new_row_');
            let rowHtml = `<tr data-pk="${pkHash}">
                <td class="text-center selectRow-column">
                    ${this.isChangesView 
                        ? `<button class="btn-discard-cell btn-discard-row-change" data-pk="${pkHash}" title="${T('Discard all changes for this row')}">
                             <i class="fa fa-undo"></i>
                           </button>` 
                        : this.renderCellHtml(pkHash, '', '','Boolean', false, false, isNewRow, 'selectRow')}
                </td>`;

            this.args.columns.forEach((colDef) => {
                const colName = colDef.title;
                const type = this.args.columnTypes[colName];
                const val = rowData[colName] || "";
                const isPk = this.args.pkColumns.includes(colName);
                const isLastUpdated = colName === this.args.lastUpdatedColumnName;

                rowHtml += `<td>${this.renderCellHtml(pkHash, colName, val, type, isPk, isLastUpdated || false, isNewRow)}</td>`;
            });
            
            return rowHtml + '</tr>';
        }

        private createReviewDataTable() {
            const reviewColumns = this.args.columns.map((colDef) => {
                const colName = colDef.title;
                const colIndex = colDef.data;
                const type = this.args.columnTypes[colName];
                const isPk = this.args.pkColumns.includes(colName);
                const isLastUpdated = colName === this.args.lastUpdatedColumnName;

                return {
                    data: colIndex,
                    title: this.getColumnTitleHtml(colName, type, isPk),
                    bSortable: false,
                    sWidth: this.getColumnWidthByType(type),
                    render: (data: any, typeInfo: string, row: any[]) => {
                        if (typeInfo !== 'display' || !row || row.length === 0) return data;
                        
                        const pkHash = String(row[0]);
                        let rowData: Record<string, string> = {};

                        if (this.store.newRows.has(pkHash)) rowData = this.store.newRows.get(pkHash) || {};
                        else if (this.store.pendingUpdates.has(pkHash) || this.store.deletedRows.has(pkHash)) rowData = this.store.originalData.get(pkHash) || {};

                        const val = rowData[colName] || "";
                        const isNewRow = pkHash.startsWith('new_row_');

                        return this.renderCellHtml(pkHash, colName, val, type, isPk, isLastUpdated || false, isNewRow);
                    }
                };
            });

            const reviewColumnsWithCheckbox = [
                {
                    data: null, title: '', bSortable: false, className: 'text-center', sWidth: '40px',
                    render: (data: any, typeInfo: string, row: any[]) => {
                        if (typeInfo !== 'display' || !row || row.length === 0) return '';
                        return `<button class="btn-discard-cell btn-discard-row-change" data-pk="${String(row[0])}" title="${T('Discard all changes for this row')}">
                                    <i class="fa fa-undo"></i>
                                </button>`;
                    }
                },
                ...reviewColumns
            ];

            this.reviewDataTable = appn.dataTable.create('#review-table', {
                bServerSide: false, bProcessing: false, scrollX: '100%', scrollY: '70vh', ordering: false,
                bPaginate: false, searching: false, bInfo: false, colResize: false, aoColumns: reviewColumnsWithCheckbox
            });
        }

        public syncUIWithState() {
            const changesCount = this.store.getChangesCount();
            const hasSelection = this.store.selectedRows.size > 0;
            
            let selectedHasNew = false, selectedHasDeleted = false, selectedHasUpdated = false, selectedHasUnmodified = false;
            
            this.store.selectedRows.forEach(pk => {
                if (pk.startsWith('new_row_')) selectedHasNew = true;
                else if (this.store.deletedRows.has(pk)) selectedHasDeleted = true;
                else if (this.store.pendingUpdates.has(pk)) selectedHasUpdated = true;
                else selectedHasUnmodified = true;
            });
            
            const canDelete = !this.isChangesView && hasSelection && (selectedHasNew || selectedHasUpdated || selectedHasUnmodified);
            const canDiscard = hasSelection && (selectedHasNew || selectedHasDeleted || selectedHasUpdated);

            $('#btn-save-changes').prop('disabled', changesCount === 0);
            $('#btn-discard-changes').toggleClass('d-none', changesCount === 0 || this.isChangesView);
            $('#btn-delete-rows, #btn-discard-selected').remove();

            const $controls = $('.dataTables_controls_start');
            if (hasSelection) {
                if (canDelete) $controls.append(`<button id="btn-delete-rows" class="btn btn-tool text-danger mr-2"><i class="fa fa-trash"></i> ${T('Delete Selected')}</button>`);
                if (canDiscard) $controls.append(`<button id="btn-discard-selected" class="btn btn-tool mr-2"><i class="fa fa-times"></i> ${T('Discard Selected')}</button>`);
            }

            $('#rows-grid tbody tr, #review-table tbody tr').each((_, el) => {
                const $tr = $(el);
                let pk = $tr.find('.cell-wrapper').first().attr('data-pk') || '';
                if (!pk) return;

                const isDeleted = this.store.deletedRows.has(pk);
                const isNewRow = pk.startsWith('new_row_');
                const isUpdated = this.store.pendingUpdates.has(pk);

                $tr.toggleClass('row-deleted', isDeleted);
                $tr.toggleClass('new-row', isNewRow);
                $tr.toggleClass('row-updated', isUpdated && !isDeleted && !isNewRow);
                $tr.toggleClass('row-selected', this.store.selectedRows.has(pk));
                $tr.find('.selectRow').prop('checked', this.store.selectedRows.has(pk));

                $tr.find('.cell-wrapper').each((_, cel) => {
                    const $wrapper = $(cel);
                    const col = $wrapper.attr('data-col') || '';
                    const isDirty = this.store.pendingUpdates.has(pk) && this.store.pendingUpdates.get(pk)![col] !== undefined;

                    $wrapper.toggleClass('input-dirty', isDirty);

                    const $ce = $wrapper.find('.cell-content-editable');
                    const $num = $wrapper.find('input[type="number"]');
                    if (isDirty) {
                        const newVal = this.store.pendingUpdates.get(pk)![col];
                        if ($ce.length && document.activeElement !== $ce[0]) $ce.text(newVal);
                        if ($num.length && document.activeElement !== $num[0]) $num.val(newVal);
                    } else if (isNewRow && this.store.newRows.has(pk)) {
                        const newVal = this.store.newRows.get(pk)![col];
                        if ($ce.length && document.activeElement !== $ce[0]) $ce.text(newVal);
                        if ($num.length && document.activeElement !== $num[0]) $num.val(newVal);
                    } else if (!isNewRow && this.store.originalData.has(pk)) {
                        const cleanVal = this.store.originalData.get(pk)![col];
                        if ($ce.length && document.activeElement !== $ce[0]) $ce.text(cleanVal);
                        if ($num.length && document.activeElement !== $num[0]) $num.val(cleanVal);
                    }

                    let currentVal = isNewRow ? (this.store.newRows.get(pk)?.[col] ?? "") : (this.store.pendingUpdates.get(pk)?.[col] ?? this.store.originalData.get(pk)?.[col] ?? "");
                    const validation = this.validateCell(pk, col, currentVal, isNewRow);
                    $wrapper.toggleClass('is-invalid', validation.shouldShowInvalid);
                });
            });

            const gridSelector = this.isChangesView ? '#review-table tbody .selectRow' : '#rows-grid tbody .selectRow';
            const $checkboxes = $(gridSelector);
            const allChecked = $checkboxes.length > 0 && $checkboxes.length === $checkboxes.filter(':checked').length;
            $('.select-all-rows').prop('checked', allChecked);
        }

        private validateCell(pk: string, col: string, val: string, isNewRow: boolean): { isValid: boolean, errorMessage?: string, shouldShowInvalid: boolean } {
            const type = this.args.columnTypes[col];
            if (isNewRow && this.args.requiredColumns.includes(col) && !val.trim()) {
                return { isValid: false, errorMessage: T('This field is required'), shouldShowInvalid: true };
            }
            if (type === 'Date' && val) {
                const ISO_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})Z$/;
                if (!ISO_REGEX.test(val.trim())) return { isValid: false, errorMessage: T('Invalid date format. Expected: yyyy-MM-ddTHH:mm:ssZ'), shouldShowInvalid: true };
            }
            if (type === 'Boolean' && val) {
                const trimmed = val.trim().toLowerCase();
                if (trimmed !== 'true' && trimmed !== 'false') return { isValid: false, errorMessage: T('Invalid boolean value. Expected: true, false, or blank'), shouldShowInvalid: true };
            }
            return { isValid: true, shouldShowInvalid: false };
        }

        private bindEvents() {
            if (!this.args.canEdit) return;

            $(document).on('input', '.cell-content-editable, input[type="number"].cell-editor', (e) => {
                const $target = $(e.target);
                const $wrapper = $target.closest('.cell-wrapper');
                const pk = $wrapper.attr('data-pk') || '', col = $wrapper.attr('data-col') || '';
                const val = $target.is('.cell-content-editable') ? $target.text() : $target.val() as string;
                this.store.updateCell(pk, col, val);
            });
            
            $(document).on('click', '.cell-view', (e) => {
                const $wrapper = $(e.target).closest('.cell-wrapper');
                const $editor = $wrapper.find('.cell-editor');
                $(e.target).addClass('d-none');
                $editor.removeClass('d-none').focus();
                if ($editor.is('input, textarea')) ($editor[0] as HTMLInputElement | HTMLTextAreaElement).select();
            });
            
            $(document).on('blur', '.cell-editor:not(:checkbox)', (e) => {
                const $this = $(e.target), $wrapper = $this.closest('.cell-wrapper');
                const pk = $wrapper.attr('data-pk') || '', col = $wrapper.attr('data-col') || '', val = $this.val() as string;
                if ($this.prop('readonly') || $this.is('input[type="number"]')) {
                    if ($this.is('input[type="number"]')) $wrapper.find('.cell-view').text(val);
                    $this.addClass('d-none'); $wrapper.find('.cell-view').removeClass('d-none');
                    if ($this.prop('readonly')) return;
                }
                const validation = this.validateCell(pk, col, val, pk.startsWith('new_row_'));
                if (validation.errorMessage && !validation.isValid) appn.toast.notify(appn.toast.Type.warning, validation.errorMessage);
                $wrapper.toggleClass('is-invalid', validation.shouldShowInvalid);
            });

            $(document).on('paste', '.cell-content-editable', (e) => {
                e.preventDefault();
                const text = (e.originalEvent as ClipboardEvent).clipboardData?.getData('text/plain') || '';
                document.execCommand('insertText', false, text);
            });

            $(document).on('change', '.selectRow', (e) => {
                const pk = $(e.target).attr('data-pk') || '';
                if ($(e.target).is(':checked')) this.store.selectedRows.add(pk);
                else this.store.selectedRows.delete(pk);
                this.syncUIWithState();
            });

            $(document).on('change', '.select-all-rows', (e) => {
                const isChecked = $(e.target).is(':checked');
                $('.select-all-rows').prop('checked', isChecked);
                const gridSelector = this.isChangesView ? '#review-table tbody .selectRow' : '#rows-grid tbody .selectRow';
                $(gridSelector).each((_, el) => {
                    const pk = $(el).attr('data-pk') || '';
                    if (isChecked) this.store.selectedRows.add(pk);
                    else this.store.selectedRows.delete(pk);
                });
                this.syncUIWithState();
            });

            $(document).on('click', '#btn-add-row', (e) => this.addNewRow(e));
            $(document).on('click', '#btn-delete-rows', (e) => this.deleteSelectedRows(e));
            $(document).on('click', '#btn-discard-selected', (e) => this.discardSelectedRows(e));
            $('#btn-discard-changes').on('click', () => this.discardAllChanges());
            $('#btn-save-changes').on('click', () => this.toggleChangesView());

            $(document).on('click', '.btn-discard-cell-change', (e) => {
                const pk = $(e.target).attr('data-pk') || '', col = $(e.target).attr('data-col') || '';
                if (this.store.pendingUpdates.has(pk)) {
                    delete this.store.pendingUpdates.get(pk)![col];
                    if (Object.keys(this.store.pendingUpdates.get(pk)!).length === 0) this.store.pendingUpdates.delete(pk);
                }
                this.showChangesInGrid(); this.syncUIWithState();
            });

            $(document).on('click', '.btn-discard-row-change', (e) => {
                const pk = $(e.target).attr('data-pk') || ''; if (!pk) return;
                this.store.deletedRows.delete(pk); this.store.newRows.delete(pk); this.store.pendingUpdates.delete(pk);
                this.showChangesInGrid(); this.syncUIWithState();
            });
        }

        private addNewRow(e: any) {
            e.preventDefault();
            const pk = `new_row_${this.newRowCounter++}`;
            const rowData: Record<string, string> = {};
            this.args.columns.forEach(c => rowData[c.title] = "");
            this.store.newRows.set(pk, rowData);
            const rowHtml = this.renderRowHtml(rowData, pk);
            $('#rows-grid tbody').prepend(rowHtml);
            $(`tr[data-pk="${pk}"]`).addClass('new-row');
            setTimeout(() => $(`tr[data-pk="${pk}"] .cell-content-editable, tr[data-pk="${pk}"] input`).first().focus().select(), 100);
            this.syncUIWithState();
        }

        public reinjectClientAddedRows() {
            if (this.store.newRows.size === 0) return;
            $('#rows-grid tbody tr.new-row').remove();
            const settings = this.dataTable.fnSettings();
            if (settings && settings._iDisplayStart > 0) return;
            this.store.newRows.forEach((rowData, pk) => {
                const rowHtml = this.renderRowHtml(rowData, pk);
                $('#rows-grid tbody').prepend(rowHtml);
                $(`tr[data-pk="${pk}"]`).addClass('new-row');
            });
        }

        private deleteSelectedRows(e: any) {
            e.preventDefault();
            this.store.selectedRows.forEach(pk => {
                if (pk.startsWith('new_row_')) { this.store.newRows.delete(pk); $(`tr[data-pk="${pk}"]`).remove(); }
                else { this.store.pendingUpdates.delete(pk); this.store.deletedRows.add(pk); }
            });
            this.store.selectedRows.clear(); this.syncUIWithState();
        }

        private discardSelectedRows(e: any) {
            e.preventDefault();
            this.store.selectedRows.forEach(pk => {
                this.store.newRows.delete(pk); this.store.deletedRows.delete(pk); this.store.pendingUpdates.delete(pk);
                if (pk.startsWith('new_row_')) $(`tr[data-pk="${pk}"]`).remove();
            });
            this.store.selectedRows.clear(); this.syncUIWithState();
            if (this.isChangesView) this.showChangesInGrid();
        }

        private discardAllChanges() {
            appn.dialog.confirm({
                title: T("Discard Changes?"), message: T("Are you sure you want to discard all pending changes including new rows?"),
                callback: (result: boolean) => { if (result) { this.store.clearAll(); this.dataTable.fnDraw(false); } }
            });
        }

        private toggleChangesView() {
            if (this.isChangesView) this.saveChanges();
            else this.showChangesInGrid();
        }

        private showChangesInGrid() {
            if (this.store.getChangesCount() === 0) { this.returnToNormalView(); return; }
            this.isChangesView = true;
            $('#grid-view').addClass('d-none'); $('#review-view').removeClass('d-none'); $('.dataTables_controls_container').addClass('d-none');
            const counts: string[] = [];
            if (this.store.newRows.size > 0) counts.push(`${this.store.newRows.size} ${T('new row(s)')}`);
            if (this.store.deletedRows.size > 0) counts.push(`${this.store.deletedRows.size} ${T('deleted row(s)')}`);
            let patchCount = 0;
            this.store.pendingUpdates.forEach((_, pk) => { if (!this.store.deletedRows.has(pk)) patchCount++ });
            if (patchCount > 0) counts.push(`${patchCount} ${T('updated row(s)')}`);
            $('#changes-counts').text(`${T('Changes pending:')} ${counts.join(', ')}. ${T('Review and apply changes to submit.')}`);
            $('#changes-warning').removeClass('d-none'); $('#btn-save-changes').html('<i class="fa fa-check"></i> ' + T('Apply Now'));
            if ($('#btn-back-to-all-rows').length === 0) {
                $('.main-header .nav-back-button').after('<a href="javascript:void(null)" id="btn-back-to-all-rows" class="nav-link"><i class="fa fa-arrow-left"></i> ' + T('Back to All Rows') + '</a>');
                $('#btn-back-to-all-rows').on('click', () => this.returnToNormalView());
            }
            $('.main-header .nav-back-button').addClass('d-none'); $('#btn-back-to-all-rows').removeClass('d-none');
            const reviewData: any[] = [];
            this.store.newRows.forEach((_, pk) => { const dr = [pk]; for (let i = 0; i < this.args.columns.length; i++) dr.push(""); reviewData.push(dr); });
            this.store.pendingUpdates.forEach((_, pk) => { if (!this.store.deletedRows.has(pk)) { const dr = [pk]; for (let i = 0; i < this.args.columns.length; i++) dr.push(""); reviewData.push(dr); } });
            this.store.deletedRows.forEach(pk => { const dr = [pk]; for (let i = 0; i < this.args.columns.length; i++) dr.push(""); reviewData.push(dr); });
            if (this.reviewDataTable) { this.reviewDataTable.fnClearTable(); this.reviewDataTable.fnAddData(reviewData); this.reviewDataTable.fnDraw(); }
            this.syncUIWithState();
        }

        private returnToNormalView() {
            this.isChangesView = false; $('#grid-view').removeClass('d-none'); $('#review-view').addClass('d-none'); $('#changes-warning').addClass('d-none');
            $('#btn-save-changes').html('<i class="fa fa-list"></i> ' + T('View Changes')); $('.main-header .nav-back-button').removeClass('d-none');
            $('#btn-back-to-all-rows').addClass('d-none'); $('.dataTables_controls_container').removeClass('d-none'); this.syncUIWithState();
        }

        private saveChanges() {
            const validation = this.validateRequiredFields();
            if (!validation.isValid) { appn.toast.notify(appn.toast.Type.error, T("Cannot save. Please fix required fields:") + "\n" + validation.errors.join("\n")); return; }
            const updates: Record<string, string>[] = [];
            this.store.pendingUpdates.forEach((changes, pk) => {
                if (this.store.deletedRows.has(pk)) return;
                const pkValues = pk.split('|'), rowUpdate: Record<string, string> = { ...changes };
                this.args.pkColumns.forEach((pkColName, idx) => { rowUpdate[pkColName] = this.store.originalData.get(pk)?.[pkColName] || pkValues[idx] || ""; });
                updates.push(rowUpdate);
            });
            const deletes: Record<string, string>[] = [];
            this.store.deletedRows.forEach(pk => {
                const pkValues = pk.split('|'), deleteFilter: Record<string, string> = {};
                this.args.pkColumns.forEach((col, idx) => deleteFilter[col] = pkValues[idx]); deletes.push(deleteFilter);
            });
            appn.dialog.showLoading();
            appn.ajax.post({
                url: this.args.updateUrl,
                data: { EntityId: this.args.entityId, Updates: updates, Inserts: Array.from(this.store.newRows.values()), Deletes: deletes },
                done: (resp: any) => {
                    appn.dialog.hideLoading();
                    if (resp.success) {
                        appn.toast.notify(appn.toast.Type.success, resp.message);
                        this.store.clearAll(); this.returnToNormalView(); this.dataTable.fnDraw(true);
                        if (this.args.onChangesApplied) this.args.onChangesApplied(resp);
                    } else appn.toast.notify(appn.toast.Type.error, resp.message);
                },
                fail: () => appn.dialog.hideLoading()
            });
        }

        private validateRequiredFields(): { isValid: boolean, errors: string[] } {
            const errors: string[] = [];
            this.store.newRows.forEach((rowData, pk) => {
                this.args.requiredColumns.forEach(colName => { if (!(rowData[colName] || "").trim()) errors.push(`${T('Row')} "${pk}": ${T('Column')} "${colName}" ${T('is required')}`); });
            });
            return { isValid: errors.length === 0, errors };
        }
    }
}
